import { useCallback, useEffect, useRef } from "react";

interface IUseInfiniteScrollOptions {
    onLoadMore: (page: number) => void;
    hasMore?: boolean;
    rootMargin?: number;
}

export const useInfiniteScroll = <T extends HTMLElement>(options: IUseInfiniteScrollOptions) => {
    const sentinelRef = useRef<T | null>(null);
    const page = useRef<number>(0);

    const observerCallback = useCallback((entries: IntersectionObserverEntry[]) => {
        const [entry] = entries;
        if (entry.isIntersecting && options.hasMore) {
            options.onLoadMore(++page.current);
        }},
        [options.onLoadMore, options.hasMore]);

    useEffect(() => {
        const observer = new IntersectionObserver(observerCallback, {
            rootMargin: options.rootMargin ? `${options.rootMargin}px` : undefined,
            threshold: 1,
        });

        if (sentinelRef.current) {
            observer.observe(sentinelRef.current);
        }

        return () => {
            if (sentinelRef.current) {
                observer.unobserve(sentinelRef.current);
            }
        };
    }, [observerCallback, options.rootMargin]);

    return sentinelRef;
};
